import { useEffect, useRef, useState } from "react";

/**
 * EIP-6963: Multi Injected Provider Discovery
 * https://eips.ethereum.org/EIPS/eip-6963
 *
 * Listens for wallet provider announcements so the UI can shows
 * every compatible wallet the user has installed instead of
 * blindly grabbing window.ethereum (which MetaMask wins).
 */

export interface EIP6963ProviderInfo {
  uuid: string;
  name: string;
  icon: string;
  rdns: string;
}

export interface EIP6963ProviderDetail {
  info: EIP6963ProviderInfo;
  provider: EIP1193Provider;
}

export interface EIP1193Provider {
  request(args: {
    method: string;
    params?: unknown[] | object;
  }): Promise<unknown>;
  on?(event: string, handler: (...args: unknown[]) => void): void;
  removeListener?(event: string, handler: (...args: unknown[]) => void): void;
}

interface EIP6963AnnounceProviderEvent extends Event {
  detail: EIP6963ProviderDetail;
}

declare global {
  interface WindowEventMap {
    "eip6963:announceProvider": EIP6963AnnounceProviderEvent;
  }
}

const PHANTOM_RDNS = "app.phantom";
const PHANTOM_FALLBACK_UUID = "phantom-window-ethereum";
// A neutral mark: the real Phantom icon arrives with its own announcement.
const PHANTOM_FALLBACK_ICON =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="8" fill="#ab9ff2"/><text x="16" y="21.5" font-family="Arial,sans-serif" font-size="15" font-weight="700" fill="#fff" text-anchor="middle">P</text></svg>',
  );

export function useEIP6963() {
  const [providers, setProviders] = useState<EIP6963ProviderDetail[]>([]);
  const seenUuids = useRef(new Set<string>());

  useEffect(() => {
    let phantomAnnounced = false;
    const handler = (event: EIP6963AnnounceProviderEvent) => {
      const { detail } = event;
      if (!detail?.info?.uuid) return;
      if (seenUuids.current.has(detail.info.uuid)) return;
      seenUuids.current.add(detail.info.uuid);
      const phantom = detail.info.rdns === PHANTOM_RDNS;
      if (phantom) phantomAnnounced = true;
      setProviders((prev) => [
        ...prev.filter(
          (entry) => !(phantom && entry.info.uuid === PHANTOM_FALLBACK_UUID),
        ),
        detail,
      ]);
    };

    window.addEventListener("eip6963:announceProvider", handler);
    window.dispatchEvent(new Event("eip6963:requestProvider"));
    // Phantom's in-app browser exposes its EVM wallet at window.phantom
    // without always announcing it, which left it out of the wallet list.
    const phantom = (window as { phantom?: { ethereum?: EIP1193Provider } })
      .phantom?.ethereum;
    if (phantom && typeof phantom.request === "function" && !phantomAnnounced)
      setProviders((prev) =>
        prev.some((entry) => entry.info.rdns === PHANTOM_RDNS)
          ? prev
          : [
              ...prev,
              {
                info: {
                  uuid: PHANTOM_FALLBACK_UUID,
                  name: "Phantom",
                  icon: PHANTOM_FALLBACK_ICON,
                  rdns: PHANTOM_RDNS,
                },
                provider: phantom,
              },
            ],
      );

    return () => {
      window.removeEventListener("eip6963:announceProvider", handler);
    };
  }, []);

  return providers;
}
